/**
 * "Tap if confirmed" reconcile affordance.
 *
 * After ~2 minutes of pending without an SSE pending_resolved, we surface a
 * small panel above the compose box that lets the user paste a tx hash to
 * mark a pending row as confirmed manually. V1_ARCHITECTURE.md Section 6
 * calls this out as a debug escape hatch — it's optional but cheap to ship.
 *
 * We don't actually call the server here (no endpoint defined for it in
 * V1.0); we just flip the local pending row to `confirmed` and remove it
 * from view. The real ThreadMessage will arrive on the next indexer tick
 * and the bubble will be replaced cleanly.
 */
import { useEffect, useState } from "react";
import { pendingStore, type PendingMessage } from "../lib/pending";

const STALE_PENDING_MS = 2 * 60 * 1000;

interface Props {
  pendingMessages: PendingMessage[];
}

export function ReconcilePrompt({ pendingMessages }: Props): JSX.Element | null {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    // Re-check every 30s — cheap, doesn't need to be precise.
    const t = setInterval(() => setNow(Date.now()), 30_000);
    return () => clearInterval(t);
  }, []);

  const stale = pendingMessages.find(
    (p) => p.status === "pending" && now - p.created_at >= STALE_PENDING_MS,
  );
  if (!stale) return null;

  return <Inner pending={stale} />;
}

function Inner({ pending }: { pending: PendingMessage }): JSX.Element {
  const [open, setOpen] = useState(false);
  const [hash, setHash] = useState("");

  function confirm(): void {
    const h = hash.trim();
    if (h.length === 0) {
      // No hash supplied — still useful as a "force confirmed" if the
      // user has visually verified it in their wallet.
      pendingStore.setStatus(pending.pending_id, "confirmed");
    } else {
      pendingStore.setStatus(pending.pending_id, "confirmed", h);
    }
    setOpen(false);
  }

  return (
    <div className="mx-3 mb-2 rounded-xl bg-tg-secondary-bg px-3 py-2 text-[12px] text-tg-text">
      <div className="flex items-center justify-between gap-2">
        <span className="text-tg-hint">
          Still waiting on confirmation? Tap to reconcile manually.
        </span>
        {!open ? (
          <button
            type="button"
            onClick={() => setOpen(true)}
            className="rounded-md bg-tg-button px-2 py-1 text-[11px] font-medium text-tg-button-text"
          >
            Reconcile
          </button>
        ) : null}
      </div>
      {open ? (
        <div className="mt-2 flex items-center gap-2">
          <input
            type="text"
            value={hash}
            onChange={(e) => setHash(e.target.value)}
            placeholder="Paste tx hash (optional)"
            className="flex-1 rounded-md bg-tg-bg px-2 py-1 font-mono text-[11px] focus:outline-none"
          />
          <button
            type="button"
            onClick={confirm}
            className="rounded-md bg-tg-button px-2 py-1 text-[11px] font-medium text-tg-button-text"
          >
            Confirm
          </button>
        </div>
      ) : null}
    </div>
  );
}
